import type { Module, RolePermissions } from '@/services/rolePermissionsService'
import { planFeatureForPath, planHasFeature } from '@/lib/platformPlanFeatures'

export type DashboardAccessContext = {
  role?: string
  permissions: RolePermissions | null
  isPlatformShop?: boolean
  planCode?: string
  planFeatures?: string[]
}

type PathRule = {
  test: (pathname: string) => boolean
  module?: Module
  roles?: string[]
  /** Any authenticated staff member (no module / role gate). */
  staffOnly?: boolean
  /** Platform shop staff or super_admin (operator ticket queue). */
  platformOperators?: boolean
  label: string
}

const MANAGER_ROLES = ['super_admin', 'admin', 'manager'] as const

/** Longest / most specific rules first. */
const PATH_RULES: PathRule[] = [
  { test: (p) => p === '/dashboard' || p === '/dashboard/', staffOnly: true, label: 'Dashboard' },
  { test: (p) => p.startsWith('/dashboard/profile'), staffOnly: true, label: 'Profile' },
  { test: (p) => p.startsWith('/dashboard/admin/support'), platformOperators: true, label: 'Platform support tickets' },
  { test: (p) => p === '/dashboard/admin' || p.startsWith('/dashboard/admin/'), roles: ['super_admin'], label: 'Platform administration' },

  { test: (p) => p === '/dashboard/shops/new' || p.startsWith('/dashboard/shops/new/'), roles: ['super_admin'], label: 'Create shop' },
  { test: (p) => p.startsWith('/dashboard/shops'), module: 'shops', label: 'Shops' },
  { test: (p) => p.startsWith('/dashboard/branches'), module: 'shops', label: 'Branches' },
  { test: (p) => p.startsWith('/dashboard/customers'), module: 'customers', label: 'Customers' },
  { test: (p) => p.startsWith('/dashboard/products'), module: 'products', label: 'Products' },
  { test: (p) => p.startsWith('/dashboard/orders'), module: 'orders', label: 'Sales Orders' },
  { test: (p) => p.startsWith('/dashboard/order-returns'), module: 'orders', label: 'Returns / RMA' },
  { test: (p) => p.startsWith('/dashboard/quotations'), module: 'orders', label: 'Quotations' },
  { test: (p) => p.startsWith('/dashboard/outbound-shipments'), module: 'orders', label: 'Outbound Shipments' },
  { test: (p) => p.startsWith('/dashboard/payments'), module: 'payments', label: 'Payments' },
  { test: (p) => p.startsWith('/dashboard/staff'), module: 'staff', label: 'Staff' },
  { test: (p) => p.startsWith('/dashboard/brands'), module: 'products', label: 'Brands' },
  { test: (p) => p.startsWith('/dashboard/suppliers'), module: 'suppliers', label: 'Suppliers' },
  { test: (p) => p.startsWith('/dashboard/purchase-orders'), module: 'suppliers', label: 'Purchase Orders' },
  { test: (p) => p.startsWith('/dashboard/subscription'), module: 'subscriptions', label: 'Subscription' },
  { test: (p) => p.startsWith('/dashboard/leases'), module: 'subscriptions', label: 'Rentals / Leases' },
  { test: (p) => p.startsWith('/dashboard/schedule'), module: 'schedules', label: 'Schedule' },
  { test: (p) => p.startsWith('/dashboard/bookings'), module: 'bookings', label: 'Bookings' },
  { test: (p) => p.startsWith('/dashboard/staff-time-entries'), module: 'staff_time_entries', label: 'Staff Time Entries' },
  { test: (p) => p.startsWith('/dashboard/vendor-bills'), module: 'journal', label: 'Vendor Bills' },
  { test: (p) => p.startsWith('/dashboard/journal'), module: 'journal', label: 'Journal' },
  { test: (p) => p.startsWith('/dashboard/tags'), module: 'tags', label: 'Tags' },
  { test: (p) => p.startsWith('/dashboard/data-management'), module: 'data_management', label: 'Data Management' },
  { test: (p) => p.startsWith('/dashboard/messaging/templates'), module: 'message_templates', label: 'Messaging Templates' },
  { test: (p) => p.startsWith('/dashboard/messaging/send'), module: 'messaging', label: 'Send Message' },
  { test: (p) => p.startsWith('/dashboard/support'), module: 'support_chat', label: 'Support Chat' },
  { test: (p) => p.startsWith('/dashboard/membership'), module: 'customers', label: 'Membership' },
  { test: (p) => p.startsWith('/dashboard/referrers'), module: 'customers', label: 'Referrers' },
  { test: (p) => p.startsWith('/dashboard/reports'), module: 'reports', label: 'Reports' },

  { test: (p) => p.startsWith('/dashboard/website'), module: 'website', label: 'Website' },
  { test: (p) => p.startsWith('/dashboard/class-packages'), module: 'class_packages', label: 'Group Programs' },
  { test: (p) => p.startsWith('/dashboard/payroll'), module: 'payroll', label: 'Payroll' },
  { test: (p) => p.startsWith('/dashboard/staff-leave'), module: 'leave', label: 'Leave' },
  { test: (p) => p.startsWith('/dashboard/settings'), roles: [...MANAGER_ROLES], label: 'Settings' },

  { test: (p) => p.startsWith('/dashboard/events'), module: 'events', label: 'Events & Tickets' },
  { test: (p) => p.startsWith('/dashboard/coupons'), module: 'coupons', label: 'Coupons' },
  { test: (p) => p.startsWith('/dashboard/teams'), module: 'teams', label: 'Teams' },
  { test: (p) => p.startsWith('/dashboard/collaboration'), module: 'teams', label: 'Collaboration Board' },
  { test: (p) => p.startsWith('/dashboard/team-files'), module: 'teams', label: 'Team Files' },
  { test: (p) => p.startsWith('/dashboard/automation-tasks'), module: 'automation', label: 'Automation Tasks' },
  { test: (p) => p.startsWith('/dashboard/companion-bundles'), module: 'companion_bundles', label: 'Companion Bundles' },
  { test: (p) => p.startsWith('/dashboard/social-promote'), module: 'social_promote', label: 'Social Promote' },
]

export function isPlatformAdmin(role?: string): boolean {
  return role === 'admin' || role === 'super_admin'
}

/** Alias for isPlatformAdmin — shop admin or platform super_admin. */
export const isApplicationAdmin = isPlatformAdmin

/** True when the user's role matches required roles; super_admin satisfies required `admin`. */
export function satisfiesRoleRequirement(
  userRole: string | undefined,
  requiredRoles: string[],
): boolean {
  if (!userRole || requiredRoles.length === 0) return requiredRoles.length === 0
  if (requiredRoles.includes(userRole)) return true
  if (userRole === 'super_admin' && requiredRoles.includes('admin')) return true
  return false
}

export function hasRoleAccess(role: string | undefined, allowedRoles: string[]): boolean {
  return satisfiesRoleRequirement(role, allowedRoles)
}

export function hasModuleView(
  permissions: RolePermissions | null,
  module: Module,
  role?: string,
): boolean {
  if (isPlatformAdmin(role)) return true
  return permissions?.[module]?.view === true
}

export function canAccessPlatformTicketQueue(ctx: Pick<DashboardAccessContext, 'role' | 'isPlatformShop'>): boolean {
  return ctx.role === 'super_admin' || ctx.isPlatformShop === true
}

function planAllowsPath(pathname: string, ctx: DashboardAccessContext): boolean {
  if (ctx.isPlatformShop || ctx.role === 'super_admin') return true
  const feature = planFeatureForPath(pathname)
  if (!feature) return true
  if (ctx.planFeatures && ctx.planFeatures.length > 0) {
    return ctx.planFeatures.includes(feature)
  }
  return planHasFeature(ctx.planCode || 'trial', feature)
}

export function resolvePathAccess(
  pathname: string,
  ctx: DashboardAccessContext,
): { allowed: boolean; label: string; reason?: 'plan' } {
  const rule: Pick<PathRule, 'staffOnly' | 'label' | 'platformOperators' | 'roles' | 'module'> =
    PATH_RULES.find((r) => r.test(pathname)) ?? { staffOnly: true, label: 'this page' }

  if (!planAllowsPath(pathname, ctx)) {
    return { allowed: false, label: rule.label, reason: 'plan' }
  }

  if (rule.platformOperators) {
    return { allowed: canAccessPlatformTicketQueue(ctx), label: rule.label }
  }

  if (rule.staffOnly) {
    return { allowed: true, label: rule.label }
  }

  if (rule.roles && hasRoleAccess(ctx.role, rule.roles)) {
    return { allowed: true, label: rule.label }
  }

  if (rule.module && hasModuleView(ctx.permissions, rule.module, ctx.role)) {
    return { allowed: true, label: rule.label }
  }

  if (rule.roles && !rule.module) {
    return { allowed: false, label: rule.label }
  }

  if (rule.module) {
    return { allowed: false, label: rule.label }
  }

  return { allowed: true, label: rule.label }
}

export type NavAccessConfig = {
  module?: Module
  roles?: string[]
  staffOnly?: boolean
  platformOperators?: boolean
}

/** Sidebar href → access config (must stay in sync with PATH_RULES). */
export const NAV_ACCESS_BY_HREF: Record<string, NavAccessConfig> = {
  '/dashboard': { staffOnly: true },
  '/dashboard/shops': { module: 'shops' },
  '/dashboard/branches': { module: 'shops' },
  '/dashboard/customers': { module: 'customers' },
  '/dashboard/products': { module: 'products' },
  '/dashboard/orders': { module: 'orders' },
  '/dashboard/quotations': { module: 'orders' },
  '/dashboard/outbound-shipments': { module: 'orders' },
  '/dashboard/payments': { module: 'payments' },
  '/dashboard/payments/intents': { module: 'payments' },
  '/dashboard/staff': { module: 'staff' },
  '/dashboard/brands': { module: 'products' },
  '/dashboard/suppliers': { module: 'suppliers' },
  '/dashboard/purchase-orders': { module: 'suppliers' },
  '/dashboard/subscription': { module: 'subscriptions' },
  '/dashboard/leases': { module: 'subscriptions' },
  '/dashboard/schedule': { module: 'schedules' },
  '/dashboard/events': { module: 'events' },
  '/dashboard/events/forms': { module: 'events' },
  '/dashboard/website': { module: 'website' },
  '/dashboard/website/blog': { module: 'website' },
  '/dashboard/website/templates': { module: 'website' },
  '/dashboard/website/analytics': { module: 'website' },
  '/dashboard/website/reviews': { module: 'website' },
  '/dashboard/class-packages': { module: 'class_packages' },
  '/dashboard/tags': { module: 'tags' },
  '/dashboard/coupons': { module: 'coupons' },
  '/dashboard/membership': { module: 'customers' },
  '/dashboard/referrers': { module: 'customers' },
  '/dashboard/teams': { module: 'teams' },
  '/dashboard/collaboration': { module: 'teams' },
  '/dashboard/team-files': { module: 'teams' },
  '/dashboard/messaging/templates': { module: 'message_templates' },
  '/dashboard/messaging/send': { module: 'messaging' },
  '/dashboard/support': { module: 'support_chat' },
  '/dashboard/payroll': { module: 'payroll' },
  '/dashboard/staff-leave': { module: 'leave' },
  '/dashboard/journal': { module: 'journal' },
  '/dashboard/vendor-bills': { module: 'journal' },
  '/dashboard/order-returns': { module: 'orders' },
  '/dashboard/bookings': { module: 'bookings' },
  '/dashboard/staff-time-entries': { module: 'staff_time_entries' },
  '/dashboard/automation-tasks': { module: 'automation' },
  '/dashboard/data-management': { module: 'data_management' },
  '/dashboard/reports': { module: 'reports' },
  '/dashboard/companion-bundles': { module: 'companion_bundles' },
  '/dashboard/social-promote': { module: 'social_promote' },
  '/dashboard/settings': { roles: [...MANAGER_ROLES] },
  '/dashboard/admin/support': { platformOperators: true },
  '/dashboard/admin': { roles: ['super_admin'] },
  '/dashboard/admin/quota': { roles: ['super_admin'] },
  '/dashboard/admin/security': { roles: ['super_admin'] },
  '/dashboard/admin/payment-gateways': { roles: ['super_admin'] },
  '/dashboard/admin/host-backups': { roles: ['super_admin'] },
  '/dashboard/admin/reports': { roles: ['super_admin'] },
  '/dashboard/admin/subscriptions': { roles: ['super_admin'] },
  '/dashboard/admin/notifications': { roles: ['super_admin'] },
  '/dashboard/admin/nurture': { roles: ['super_admin'] },
}

export function canAccessNavHref(href: string, ctx: DashboardAccessContext): boolean {
  if (!planAllowsPath(href, ctx)) return false
  const config = NAV_ACCESS_BY_HREF[href]
  if (!config) return true

  if (config.platformOperators) return canAccessPlatformTicketQueue(ctx)
  if (config.staffOnly) return true
  if (config.roles && hasRoleAccess(ctx.role, config.roles)) return true
  if (config.module && hasModuleView(ctx.permissions, config.module, ctx.role)) return true
  if (config.roles && !config.module) return false
  if (config.module) return false
  return true
}
